import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { PageContext } from 'pages/InstructorViewAssignment/PageProvider';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from 'services/api';
import submissionService from 'services/submission';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    Loading,
    Paper,
    Stack,
    styled,
    Text,
    Tooltip,
} from 'shell/components';
import { getIconKind } from 'shell/components/ResourceChip';
import { SUBMISSION_ALLOWED_FILE_TYPES } from 'utils/constants';
import { getPrettyFileSize, getPrettyFileType, isAllowedMimeType, lowercaseExtension } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const MAX_FILE_SIZE = 32 * 1_000_000; // 32 MB
const MAX_VIDEO_FILE_SIZE = 300 * 1_000_000; // 300 MB
const MAX_SUBMISSION_SIZE = 500 * 1_000_000; // 500 MB
const FILE_SIZE_ERROR = 'Exceeds file size';
const FILE_TYPE_ERROR = 'Unsupported file';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const getRetryAfterMs = (err, fallbackMs) => {
    // works for fetch or axios-ish errors
    const headers = err?.response?.headers || err?.headers || (err?.response?.raw && err.response.raw.headers);

    let ra = null;
    if (headers) {
        // fetch Headers.get(...)
        if (typeof headers.get === 'function') ra = headers.get('retry-after');
        // axios plain object
        if (!ra && headers['retry-after']) ra = headers['retry-after'];
    }
    const s = Number(ra);
    return !Number.isNaN(s) && s > 0 ? s * 1000 : fallbackMs;
};

const withRetry = async (fn, opts = {}) => {
    const {
        retries = 5,
        baseDelayMs = 500, // starting backoff
        maxDelayMs = 15000, // cap
        jitterMs = 250,
    } = opts;

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            return await fn();
        } catch (err) {
            const status = err?.response?.status || err?.status || 0;
            if (attempt === retries) throw err;

            // exponential backoff + jitter; honor Retry-After on 429/503 if present
            let delay = Math.min(baseDelayMs * Math.pow(2, attempt), maxDelayMs) + Math.floor(Math.random() * jitterMs);

            if (status === 429 || status === 503) {
                delay = getRetryAfterMs(err, delay);
            }
            await sleep(delay);
        }
    }
};

const mapWithConcurrency = async (items, limit, worker, spacingMs = 0) => {
    const out = new Array(items.length);
    let cursor = 0;

    async function run() {
        while (true) {
            const i = cursor++;
            if (i >= items.length) return;
            try {
                out[i] = await worker(items[i], i);
            } catch (e) {
                // Decide: either store the error or rethrow. Here we store.
                out[i] = { error: true, message: e?.message || 'Request failed', raw: e };
            }
            if (spacingMs) await sleep(spacingMs);
        }
    }

    const runners = Array.from({ length: Math.min(limit, items.length) }, run);
    await Promise.all(runners);
    return out;
};

const Folder = ({ folder, onClickDeleteFolder, onClickDeleteFile }) => {
    const { t } = useTranslation('assignment');

    const submitterName = folder.name;
    const folderSizeMB = getPrettyFileSize(
        folder.files.map(({ data }) => data).reduce((acc, cur) => acc + cur.size, 0),
        true
    );
    const hasError = Boolean(folder.error);
    const hasFileError = folder.files.some((file) => Boolean(file.error));
    return (
        <Paper
            sx={(theme) => ({
                ...((hasError || hasFileError) && {
                    borderColor: theme.palette.ensightful.error[500],
                }),
            })}
            variant={'outlined'}
        >
            <Accordion>
                <AccordionSummary>
                    <Stack
                        sx={{ width: '100%' }}
                        alignItems={'center'}
                        direction={'row'}
                        justifyContent={'space-between'}
                        spacing={4}
                    >
                        <Stack alignItems={'center'} direction={'row'} spacing={3}>
                            <Icon
                                sx={(theme) => ({
                                    borderRadius: '4px',
                                    width: 32,
                                    height: 32,
                                    padding: '5px',
                                    background: theme.palette.ensightful.warning[500],
                                    color: theme.palette.ensightful.shades[0],
                                    ...(hasError && {
                                        padding: 0,
                                        background: theme.palette.ensightful.shades[0],
                                        color: theme.palette.ensightful.error[600],
                                    }),
                                })}
                                kind={hasError ? 'error' : 'folder'}
                            />
                            <Stack>
                                <Text>{submitterName}</Text>
                                <Text color={hasError ? 'error' : 'light'}>
                                    {t('submission.folder-total-size-label', 'Folder (Total {{size}})', {
                                        size: folderSizeMB,
                                    })}
                                </Text>
                            </Stack>
                        </Stack>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            {(hasError || hasFileError) && (
                                <Tooltip
                                    sx={{ borderRadius: '4px', fontSize: '1rem', padding: '1px' }}
                                    content={<Text color={'inherit'}>{folder.error ?? 'Import failed'}</Text>}
                                    type={'alert'}
                                />
                            )}
                            <IconButton
                                aria-label={'delete folder'}
                                kind={'delete'}
                                size={'small'}
                                variant={'square'}
                                onClick={(e) => onClickDeleteFolder(e, folder.id)}
                            />
                        </Stack>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ mt: 4, ml: 3 }}>
                    <Stack spacing={3}>
                        {folder.files.map((file) => {
                            const fileName = file.data.name;
                            const fileType = getPrettyFileType(file.data.type, t);
                            const fileSizeMB = getPrettyFileSize(file.data.size, true);
                            const hasError = Boolean(file.error);
                            return (
                                <Stack
                                    key={file.id}
                                    sx={(theme) => ({
                                        padding: 1,
                                        ...(hasError && {
                                            borderRadius: '8px',
                                            background: theme.palette.ensightful.error[100],
                                        }),
                                    })}
                                    alignItems={'center'}
                                    direction={'row'}
                                    spacing={3}
                                >
                                    <Icon sx={{ width: 32, height: 32 }} kind={getIconKind('file', file)} />
                                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                                        <Text sx={{ width: 'fit-content' }} noWrap tooltip={{ component: fileName }}>
                                            {fileName}
                                        </Text>
                                        <Stack direction={'row'}>
                                            <Text color={'light'}>
                                                {fileType} ({fileSizeMB})
                                            </Text>
                                            {file.error && (
                                                <>
                                                    <Icon
                                                        sx={(theme) => ({ color: theme.palette.ensightful.error[700] })}
                                                        kind={'error'}
                                                    />
                                                    <Text color={'error'} component={'span'}>
                                                        {file.error}
                                                    </Text>
                                                </>
                                            )}
                                        </Stack>
                                    </Stack>
                                    <IconButton
                                        sx={(theme) => ({
                                            ...(hasError && {
                                                color: theme.palette.ensightful.error[700],
                                                '&:hover': {
                                                    color: theme.palette.ensightful.error[700],
                                                },
                                            }),
                                        })}
                                        aria-label={'delete file'}
                                        kind={'delete'}
                                        size={'small'}
                                        variant={'square'}
                                        onClick={() => onClickDeleteFile(folder.id, file.id)}
                                    />
                                </Stack>
                            );
                        })}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Paper>
    );
};

const UploadAPISubmissionsDialog = ({ open, assignment, onClose, onClick }) => {
    const { course, setSubmissions } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const { apiSubmissions, setAPISubmissions } = useContext(PageContext);

    const isCancelled = useRef(false);
    const [loading, setLoading] = useState(false);
    const [folders, setFolders] = useState(null);

    const hasError = folders?.some((folder) => {
        return Boolean(folder.error) || folder.files.some((file) => Boolean(file.error));
    });
    const numFolders = folders?.length ?? 0;
    const numFiles = folders?.reduce((acc, cur) => acc + cur.files.length, 0) ?? 0;
    const numErrorFolders = folders?.filter(({ error }) => Boolean(error))?.length ?? 0;
    const numErrorFiles =
        folders?.reduce((acc, cur) => {
            return acc + cur.files.filter(({ error }) => Boolean(error))?.length;
        }, 0) ?? 0;

    const handleClose = () => {
        setTimeout(() => {
            setLoading(false);
            setAPISubmissions(null);
            setFolders(null);
        }, 150);
        onClose();
    };

    const handleClickUpload = async () => {
        setLoading(true);
        try {
            await Promise.all(apiSubmissions.toDelete.map(({ id }) => submissionService.deleteSubmission(id)));
            setSubmissions((current) => {
                return current.filter((submission) => {
                    if (apiSubmissions.toDelete.some(({ id }) => id === submission.id)) return false;
                    return true;
                });
            });
            onClick(
                folders.map((folder) => {
                    return {
                        submitted_at: folder.files[0].submitted_at,
                        files: folder.files.map((file) => omit(file, ['error', 'submitted_at'])),
                    };
                })
            );
            handleClose();
        } catch (e) {
            console.error(e);
            message.error(
                t('common:message.something-went-wrong-try-again', 'Something went wrong. Please try again later.')
            );
        }
    };

    const getSubmissionFiles = useCallback(
        async (course_id, accessToken, assignment, submission, tries) => {
            if (tries > 20) return message.error(t('common:message.something-went-wrong', 'Something went wrong.'));
            const jitter = Math.floor(Math.random() * (50 - 1 + 1) + 1);
            try {
                const res = await apiService.getSubmissionFile({
                    course_id,
                    accessToken,
                    assignment,
                    submission,
                });
                return res;
            } catch (e) {
                tries = tries + 1;
                return setTimeout(() => {
                    return getSubmissionFiles(course_id, accessToken, assignment, submission, tries);
                }, jitter);
            }
        },
        [message, t]
    );

    const getFileD2L = useCallback(
        async (course_id, accessToken, tries, timestamp, userSubmission) => {
            const latestSubmission = userSubmission.Submissions[0];
            return await Promise.all(
                latestSubmission.Files.map(async (file) => {
                    const res = await getSubmissionFiles(
                        course_id,
                        accessToken,
                        assignment,
                        { Id: latestSubmission.Id, ...file },
                        tries
                    );
                    const apiName = userSubmission?.Entity?.DisplayName
                        ? `${userSubmission.Entity.DisplayName} (${userSubmission.Entity.EntityId})`
                        : `${userSubmission.Entity.EntityId}`;
                    return {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        owner_account_id: auth.id,
                        course_id: course.id,
                        assignment_id: assignment.id,
                        data: res,
                        submitted_at: latestSubmission.SubmissionDate,
                        api: {
                            user: {
                                id: userSubmission.Entity.EntityId,
                                name: apiName,
                            },
                            file: {
                                id: file.FileId,
                                submission_id: latestSubmission.Id,
                                name: lowercaseExtension(file.FileName),
                                size: res.size,
                                type: res.type,
                                submitted_on: latestSubmission.SubmissionDate,
                            },
                        },
                    };
                })
            );
        },
        [assignment, auth.id, course.id, getSubmissionFiles]
    );

    const getFileCanvas = useCallback(
        async (course_id, accessToken, tries, timestamp, userSubmission) => {
            return await Promise.all(
                userSubmission.attachments.map(async (attachment) => {
                    const submission = attachment;
                    submission.fileName = lowercaseExtension(submission.filename);
                    const res = await getSubmissionFiles(course_id, accessToken, assignment, submission, tries);
                    const type = submission['content-type'];
                    const result = {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        owner_account_id: auth.id,
                        course_id: course.id,
                        assignment_id: assignment.id,
                        data: res,
                        submitted_at: userSubmission.submitted_at,
                    };
                    const apiName = userSubmission?.user?.name
                        ? `${userSubmission?.user?.sortable_name || userSubmission?.user?.name} (${userSubmission.user_id})`
                        : `${userSubmission.user_id}`;
                    if (userSubmission.group?.id) {
                        result.api = {
                            group: userSubmission.group,
                            user: {
                                id: `${userSubmission.user_id}`,
                                name: apiName,
                                sections: userSubmission.sections,
                            },
                            file: {
                                id: `${submission.id}`,
                                name: lowercaseExtension(submission.filename),
                                size: res.size,
                                type: type,
                                attempt: userSubmission.attempt,
                            },
                        };
                    } else {
                        result.api = {
                            user: {
                                id: `${userSubmission.user_id}`,
                                name: apiName,
                                sections: userSubmission.sections,
                            },
                            file: {
                                id: `${submission.id}`,
                                name: lowercaseExtension(submission.filename),
                                size: res.size,
                                type: type,
                                attempt: userSubmission.attempt,
                            },
                        };
                    }
                    return result;
                })
            );
        },
        [assignment, auth.id, course.id, getSubmissionFiles]
    );

    const fetchFilesSafely = useCallback(
        async ({ apiSubmissions, course_id, accessToken, tries, timestamp }) => {
            const CONCURRENCY = 10; // tune based on API tolerance
            const SPACING_MS = 300; // ~<= (CONCURRENCY / (1000 / SPACING_MS)) req/s overall
            const RETRY_OPTS = { retries: 5, baseDelayMs: 600, maxDelayMs: 20000, jitterMs: 300 };

            const getFile = (userSubmission) => {
                return withRetry(() => {
                    if (process.env.REACT_APP_KIND === 'D2L') {
                        return getFileD2L(course_id, accessToken, tries, timestamp, userSubmission);
                    } else {
                        return getFileCanvas(course_id, accessToken, tries, timestamp, userSubmission);
                    }
                }, RETRY_OPTS);
            };

            const newFiles = await mapWithConcurrency(apiSubmissions, CONCURRENCY, getFile, SPACING_MS);
            return newFiles;
        },
        [getFileCanvas, getFileD2L]
    );

    // validate each file as well as the entire submission
    // each file must have an accepted file type
    // each file must be under a certain size
    // a submission (all files together) must be under a certain size
    // if a file is not valid, store the error in file.error
    // return updated submissions
    const validateSubmissions = useCallback(
        (submissions) => {
            const isFileTypeValid = (file) => isAllowedMimeType(file.data.type, SUBMISSION_ALLOWED_FILE_TYPES);

            const isFileSizeValid = (file) => {
                if (file.data.type.startsWith('video') || file.data.type === 'application/mp4') {
                    return file.data.size <= MAX_VIDEO_FILE_SIZE;
                }
                return file.data.size <= MAX_FILE_SIZE;
            };

            const validateSubmissionSize = (files) => {
                const totalSize = files.reduce((acc, file) => acc + file.data.size, 0);

                if (totalSize > MAX_SUBMISSION_SIZE) {
                    return t('submission.exceeds-folder-size', 'Exceeds folder size');
                }

                return null;
            };

            return submissions.map((submission) => {
                const validatedFiles = submission.files.map((file) => {
                    let error = null;

                    if (!isFileTypeValid(file)) {
                        error = FILE_TYPE_ERROR;
                    } else if (!isFileSizeValid(file)) {
                        error = FILE_SIZE_ERROR;
                    }

                    return { ...file, error };
                });

                const submissionError = validateSubmissionSize(validatedFiles);

                return {
                    ...submission,
                    files: validatedFiles,
                    error: submissionError,
                };
            });
        },
        [t]
    );

    const handleClickDeleteFiles = () => {
        const numFailedType = folders.reduce((acc, cur) => {
            return acc + cur.files.filter(({ error }) => error === FILE_TYPE_ERROR).length;
        }, 0);
        const numFailedSize = folders.reduce((acc, cur) => {
            return acc + cur.files.filter(({ error }) => error === FILE_SIZE_ERROR).length;
        }, 0);
        const numFailedTotal = numFailedType + numFailedSize;
        setDialog({
            open: true,
            message: t('dialog.delete-files-warning.title', 'Delete All Failed Files?'),
            description: (
                <Stack>
                    <Text>
                        {t(
                            'dialog.delete-files-warning.description',
                            '({numFailedTotal}) file(s) failed to import. Delete them now?',
                            { count: numFailedTotal }
                        )}
                    </Text>
                    <Stack alignItems={'flex-start'} component={'ul'}>
                        <Text component={'li'}>
                            {t('dialog.delete-files-warning.unsupported-file-type', 'Unsupported files ({count})', {
                                count: numFailedType,
                            })}
                        </Text>
                        <Text component={'li'}>
                            {t(
                                'dialog.delete-files-warning.file-exceed-size-limit',
                                'Files exceeding size limits ({count})',
                                { count: numFailedSize }
                            )}
                        </Text>
                    </Stack>
                </Stack>
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setFolders((current) => {
                    return current
                        .filter((folder) => {
                            if (folder.error) return false;
                            if (folder.files.every((file) => file.error)) return false;
                            return true;
                        })
                        .map((folder) => {
                            return {
                                ...folder,
                                files: folder.files.filter((file) => !Boolean(file.error)),
                            };
                        });
                });
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickDeleteFolder = (e, folderId) => {
        e.stopPropagation();
        setDialog({
            open: true,
            message: t('dialog.delete-folder-warning.title', 'Delete Folder?'),
            description: t('dialog.delete-folder-warning.description', 'Are you sure you want to delete this folder?'),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setFolders((current) => current.filter(({ id }) => id !== folderId));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickDeleteFile = (folderId, fileId) => {
        const isLastFile = folders.find(({ id }) => id === folderId).files.every(({ id }) => id === fileId);
        setDialog({
            open: true,
            message: t('dialog.delete-single-file-warning.title', 'Delete File?'),
            description: t(
                'dialog.delete-single-file-warning.description',
                'Are you sure you want to delete this file?'
            ),
            note: isLastFile
                ? t(
                      'dialog.delete-single-file-warning.note',
                      'Note: Since this is the only file, deleting this file will also delete the folder.'
                  )
                : '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setFolders((current) => {
                    const newFolders = isLastFile
                        ? current.filter(({ id }) => id !== folderId)
                        : current.map((folder) => {
                              if (folder.id !== folderId) return folder;
                              return {
                                  ...folder,
                                  files: folder.files.filter(({ id }) => id !== fileId),
                              };
                          });
                    const validatedNewFolders = validateSubmissions(newFolders);
                    return validatedNewFolders;
                });
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    useEffect(() => {
        if (!open) return;
        (async () => {
            const course_id = auth.course_id;
            const accessToken = localStorage.getItem('access_token');
            const timestamp = new Date().getTime();
            let tries = 0;
            const submissions = await fetchFilesSafely({
                apiSubmissions: apiSubmissions.toCreate,
                course_id,
                accessToken,
                tries,
                timestamp,
            });
            const folders = submissions.map((files) => {
                return {
                    id: files[0].api.user.id,
                    name: files[0].api.user.name,
                    files,
                    submitted_at: files[0].submitted_at,
                };
            });
            const validatedFolders = validateSubmissions(folders);
            validatedFolders.sort((a, b) => {
                const getPriority = (submission) => {
                    if (submission.error) return 0;
                    const hasFileError = submission.files.some((file) => file.error);
                    if (hasFileError) return 1;
                    return 2;
                };
                return getPriority(a) - getPriority(b);
            });
            if (isCancelled.current) return;
            setFolders(validatedFolders);
        })();
    }, [open, fetchFilesSafely, auth.course_id, apiSubmissions?.toCreate, validateSubmissions]);

    useEffect(() => {
        isCancelled.current = !open;
    }, [open]);

    return (
        <CustomDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                    <Stack spacing={3}>
                        <Stack spacing={1}>
                            <Text component={'h1'} variant={'h6'} weight={'medium'}>
                                {t('submission.import-submissions', 'Import Submissions')}
                            </Text>
                            <Text>
                                <Text component={'span'} weight={'medium'}>
                                    {t('submission.import-limits', 'Import limits')}:
                                </Text>{' '}
                                {getPrettyFileSize(MAX_VIDEO_FILE_SIZE, true, 0)}/{t('common:ui.video', 'video')} ·{' '}
                                {getPrettyFileSize(MAX_FILE_SIZE, true, 0)}/{t('common:ui.file', 'file')} ·{' '}
                                {getPrettyFileSize(MAX_SUBMISSION_SIZE, true, 0)}/{t('common:ui.folder', 'folder')}
                            </Text>
                        </Stack>
                        <Stack spacing={1}>
                            <Text color={'light'}>
                                {t('submission.folders-label', 'Folders')}: {numFolders - numErrorFolders}{' '}
                                {numErrorFolders ? (
                                    <Text color={'error'} component={'span'}>
                                        ({t('common:ui.failed-count', '{count} failed', { count: numErrorFolders })})
                                    </Text>
                                ) : null}
                            </Text>
                            <Text color={'light'}>
                                {t('submission.total-files-label', 'Total Files')}: {numFiles - numErrorFiles}{' '}
                                {numErrorFiles ? (
                                    <Text color={'error'} component={'span'}>
                                        ({t('common:ui.failed-count', '{count} failed', { count: numErrorFiles })})
                                    </Text>
                                ) : null}
                            </Text>
                        </Stack>
                    </Stack>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                {folders ? (
                    <Stack spacing={3}>
                        {folders.map((folder) => {
                            return (
                                <Folder
                                    key={folder.id}
                                    folder={folder}
                                    onClickDeleteFolder={handleClickDeleteFolder}
                                    onClickDeleteFile={handleClickDeleteFile}
                                />
                            );
                        })}
                    </Stack>
                ) : (
                    <Loading />
                )}
            </DialogContent>
            <Divider />
            <DialogActions>
                <Stack sx={{ width: '100%' }} alignItems={'center'} direction={'row-reverse'} spacing={3}>
                    <Button
                        disabled={!folders?.length || hasError}
                        loading={loading}
                        loadingGrow={loading}
                        onClick={handleClickUpload}
                    >
                        {t('submission.upload-submissions', 'Upload Submissions')}
                    </Button>
                    {hasError && (
                        <Button color={'destructive'} variant={'secondary'} onClick={handleClickDeleteFiles}>
                            {t('controls.delete-files', 'Delete Files')}
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </CustomDialog>
    );
};

const CustomDialog = styled(Dialog)(() => {
    return {
        '& .MuiDialog-paper': {
            width: '540px',
            height: '600px',
        },
    };
});

export default UploadAPISubmissionsDialog;
